
import React, { useEffect, useState } from 'react';

interface InitializationScreenProps {
    onForceEnter?: () => void;
    onComplete?: () => void;
    statusMessage?: string;
}

const InitializationScreen: React.FC<InitializationScreenProps> = ({ onForceEnter, onComplete, statusMessage }) => {
    const [progress, setProgress] = useState(0);
    const [showOverride, setShowOverride] = useState(false);

    useEffect(() => {
        // Progress simulation
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setTimeout(() => onComplete?.(), 500); // Small delay before unmounting
                    return 100;
                }
                // Non-linear progress for realism
                const increment = Math.random() * 5;
                return Math.min(prev + increment, 100);
            });
        }, 150);

        // Force entry timer
        const timeout = setTimeout(() => setShowOverride(true), 10000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    return (
        <div className="fixed inset-0 z-[150] bg-black flex flex-col items-center justify-center overflow-hidden">

            {/* Background Grid FX */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#000_100%)] z-10"></div>
            <div className="absolute inset-0 bg-[linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,240,255,0.05)_1px,transparent_1px)] bg-[size:30px_30px] opacity-20"></div>

            {/* MAIN CIRCULAR LOADER */}
            <div className="relative w-80 h-80 flex items-center justify-center scale-75 md:scale-100">

                {/* Ring 1: Outer Dashed - Slow Rotate */}
                <div className="absolute inset-0 rounded-full border border-dashed border-primary/30 animate-[spin_20s_linear_infinite]"></div>

                {/* Ring 2: Middle Thin - Reverse Fast */}
                <div className="absolute inset-4 rounded-full border border-primary/20 border-t-transparent border-b-transparent animate-[spin_3s_linear_infinite_reverse]"></div>

                {/* Ring 3: Progress Arc */}
                <svg className="absolute inset-0 w-full h-full -rotate-90 drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]">
                    <circle cx="50%" cy="50%" r="45%" fill="none" stroke="#111" strokeWidth="4" />
                    <circle
                        cx="50%" cy="50%" r="45%" fill="none" stroke="#00F0FF" strokeWidth="4"
                        strokeDasharray="283"
                        strokeDashoffset={283 - (283 * progress) / 100}
                        strokeLinecap="round"
                        className="transition-all duration-200 ease-out"
                    />
                </svg>

                {/* Central Core */}
                <div className="absolute w-40 h-40 bg-black/50 backdrop-blur-md rounded-full border border-primary/50 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(0,240,255,0.2)] overflow-hidden">
                    <div className="absolute inset-0 bg-primary/10 animate-pulse"></div>

                    {/* Data Stream Effect */}
                    <div className="absolute top-0 w-full h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent animate-[scan_1.5s_linear_infinite]"></div>

                    <div className="relative z-10 text-center">
                        <span className="text-4xl font-bold font-mono text-white tracking-tighter">{Math.floor(progress)}%</span>
                        <span className="text-[8px] text-primary font-mono tracking-widest block mt-1">LOADING</span>
                    </div>
                </div>

                {/* Orbiting Particles */}
                <div className="absolute w-full h-full animate-[spin_5s_linear_infinite]">
                    <div className="absolute top-0 left-1/2 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white] -translate-x-1/2 -translate-y-1"></div>
                </div>
                <div className="absolute w-[80%] h-[80%] animate-[spin_8s_linear_infinite_reverse]">
                    <div className="absolute bottom-0 left-1/2 w-1.5 h-1.5 bg-primary rounded-full shadow-[0_0_10px_#00F0FF] -translate-x-1/2 translate-y-1"></div>
                </div>

            </div>

            {/* Text Logs - NOW DYNAMIC BASED ON APP STATE */}
            <div className="mt-12 text-center h-10 relative z-20">
                <div className="text-primary font-mono text-sm tracking-[0.2em] animate-pulse">
                    {statusMessage || "INITIALIZING CORE..."}
                </div>
                <div className="text-[10px] text-gray-500 font-mono mt-2">
                    SYSTEM ID: 8X-92-ALPHA
                </div>
            </div>

            {/* Override Button */}
            {showOverride && onForceEnter && (
                <button
                    onClick={onForceEnter}
                    className="mt-8 px-6 py-2 border border-red-500/50 text-red-500 text-[10px] font-bold tracking-widest hover:bg-red-500 hover:text-white transition-all rounded animate-fade-in"
                >
                    ⚠ BYPASS BOOT SEQUENCE
                </button>
            )}

        </div>
    );
};

export default InitializationScreen;
